import React, { useState } from 'react';
import { Pagination, Select, Input, Spin, Modal, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getStyleColorLibraryList, deleteStyleColorLibrary } from '../../apis/styleColorLibraryApi';
import { useFetchCompanyCode } from '../../hooks/useTasks';
import SpinLoader from '../../commonComponents/SpinLoader';
import useLogout from '../../hooks/useLogout';

export default function StyleColorLibraryPage() {
    const { logout } = useLogout();
    const queryClient = useQueryClient();
    const [companyCode, setCompanyCode] = useState('');
    const [keySearch, setKeySearch] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const fetchCompanyCodeError = (err) => {
        if (err?.response?.status === 401) logout();
    };
    const { data: companyCodeList } = useFetchCompanyCode(fetchCompanyCodeError);

    const {
        data: listData,
        isLoading,
        refetch,
    } = useQuery(
        ['styleColorLibrary', companyCode, keySearch, pageNumber, pageSize],
        getStyleColorLibraryList,
        { refetchOnWindowFocus: false }
    );

    const deleteMutation = useMutation(deleteStyleColorLibrary, {
        onSuccess: () => {
            queryClient.invalidateQueries(['styleColorLibrary']);
        },
    });

    const totalCount = listData?.totalCount ?? 0;
    const data = listData?.data ?? [];

    const handleDelete = (item) => {
        Modal.confirm({
            title: 'Delete entry',
            content: `Delete "${item.key}" (${item.companyCode})? This will soft-delete the entry.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => deleteMutation.mutate(item._id),
        });
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginBottom: 12,
                }}
            >
                <Input
                    value={keySearch}
                    size="small"
                    placeholder="Style / Color code (search key)"
                    style={{ width: 220 }}
                    onChange={(e) => {
                        setKeySearch(e.target.value);
                        setPageNumber(1);
                    }}
                />
                <span>Company</span>
                <Select
                    placeholder="Select company code"
                    onChange={(val) => {
                        setCompanyCode(val ?? '');
                        setPageNumber(1);
                    }}
                    value={companyCode || undefined}
                    style={{ minWidth: 160 }}
                    allowClear
                >
                    <Select.Option key="all" value="">
                        All
                    </Select.Option>
                    {companyCodeList?.map((el) => (
                        <Select.Option key={el} value={el}>
                            {el}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    backgroundColor: '#E7E6E9',
                    minHeight: 200,
                    overflow: 'auto',
                    padding: 10,
                }}
            >
                {isLoading ? (
                    <div className="spin-center">
                        <SpinLoader />
                    </div>
                ) : data.length > 0 ? (
                    data.map((item) => (
                        <div
                            key={item._id}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                backgroundColor: 'white',
                                margin: 10,
                                padding: 10,
                                borderRadius: 5,
                                minWidth: 160,
                                maxWidth: 200,
                            }}
                        >
                            {
                                /*<Tooltip title="Delete">
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 4,
                                            top: 4,
                                            backgroundColor: 'rgba(185, 169, 169, 0.24)',
                                            padding: 4,
                                            color: '#575454',
                                            borderRadius: 3,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleDelete(item)}
                                    >
                                        <DeleteOutlined />
                                    </div>
                                </Tooltip>
                                */
                            }
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4,
                                    justifyContent: 'center',
                                    minHeight: 80,
                                    marginBottom: 8,
                                }}
                            >
                                {item.images?.length > 0 ? (
                                    item.images.slice(0, 3).map((img) => (
                                        <img
                                            key={img._id}
                                            src={img.url}
                                            alt=""
                                            style={{
                                                width: 60,
                                                height: 80,
                                                objectFit: 'contain',
                                                borderRadius: 4,
                                            }}
                                        />
                                    ))
                                ) : (
                                    <div
                                        style={{
                                            width: 60,
                                            height: 80,
                                            background: '#f0f0f0',
                                            borderRadius: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 11,
                                            color: '#999',
                                        }}
                                    >
                                        No image
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'center', width: '100%' }}>
                                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.key}
                                </div>
                                <div style={{ fontSize: 12, color: '#666' }}>{item.companyCode}</div>
                                {item.type && (
                                    <div style={{ fontSize: 12, marginTop: 2, textTransform: 'capitalize' }}>{item.type}</div>
                                )}
                                <div style={{ fontSize: 12, marginTop: 2 }}>
                                    <span
                                        style={{
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            backgroundColor: item.status === 'created' ? '#d4edda' : '#fff3cd',
                                            color: item.status === 'created' ? '#155724' : '#856404',
                                        }}
                                    >
                                        {item.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <p style={{ padding: 20 }}>No data found.</p>
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <Pagination
                    current={pageNumber}
                    pageSize={pageSize}
                    total={totalCount}
                    onChange={(page) => setPageNumber(page)}
                    showSizeChanger
                    pageSizeOptions={['20', '50', '100']}
                    onShowSizeChange={(current, size) => {
                        setPageNumber(1);
                        setPageSize(size);
                    }}
                    disabled={isLoading}
                />
            </div>
        </>
    );
}
